import * as React from 'react'
import { createBrowserRouter } from 'react-router-dom'

import AuthRoutes from './AuthRoutes'
import AuthLayout from 'components/Layout/AuthLayout'
import { createLazyRoute, LOADING_MESSAGES } from './routeUtils'

// Lazy load all route components with optimized loading messages
// Auth components
const FirstPage = createLazyRoute(() => import('views/auth/FirstPage'), LOADING_MESSAGES.AUTH)
const Login = createLazyRoute(() => import('views/auth/Login'), LOADING_MESSAGES.AUTH)
const Register = createLazyRoute(() => import('views/auth/Register'), LOADING_MESSAGES.AUTH)
const ResetPassword = createLazyRoute(() => import('views/auth/ResetPassword'), LOADING_MESSAGES.AUTH)
const Verification = createLazyRoute(() => import('views/auth/Verification'), LOADING_MESSAGES.AUTH)
const CodeVerification = createLazyRoute(() => import('views/auth/CodeVerification'), LOADING_MESSAGES.AUTH)
const MagicLinkVerification = createLazyRoute(() => import('views/auth/MagicLinkVerification'), LOADING_MESSAGES.AUTH)
const ForgotPassword = createLazyRoute(() => import('views/auth/ForgotPassword'), LOADING_MESSAGES.AUTH)
const ContractorRegisterForm = createLazyRoute(() => import('views/auth/contractor/RegisterForm'), LOADING_MESSAGES.AUTH)
const EmployeeRegisterForm = createLazyRoute(() => import('views/auth/employee/RegisterForm'), LOADING_MESSAGES.AUTH)
const GoogleSignUpForm = createLazyRoute(() => import('views/auth/GoogleSignUpForm'), LOADING_MESSAGES.AUTH)

// Employer components
const AddJobPost = createLazyRoute(() => import('views/employer/add-job-post/index'), LOADING_MESSAGES.CANDIDATES)
const PricingPlans = createLazyRoute(() => import('views/employer/add-job-post/PricingPlans'), LOADING_MESSAGES.PRICING_PLANS)
const CheckoutPlan = createLazyRoute(() => import('views/employer/add-job-post/CheckoutPlan'), LOADING_MESSAGES.CHECKOUT_PLAN)
const PricingPlansInApp = createLazyRoute(() => import('views/employer/add-job-post/PricingPlansInApp'), LOADING_MESSAGES.PRICING_PLANS)
const FindCandidates = createLazyRoute(() => import('views/employer/candidates/index'), LOADING_MESSAGES.CANDIDATES)
const EmployerLayout = createLazyRoute(() => import('views/employer/index'), LOADING_MESSAGES.EMPLOYER)
const Settings = createLazyRoute(() => import('views/employer/setting/index'), LOADING_MESSAGES.SETTINGS)
const Upgrade = createLazyRoute(() => import('views/employer/setting/Upgrade'), LOADING_MESSAGES.SETTINGS)
const PremiumVideo = createLazyRoute(() => import('views/employer/PremiumVideo'), LOADING_MESSAGES.EMPLOYER)
const EmployerMessages = createLazyRoute(() => import('views/employer/messages'), LOADING_MESSAGES.MESSAGES)
const EmployerProfileContainer = createLazyRoute(() => import('views/employer/profile'), LOADING_MESSAGES.PROFILE)
const EmployerProfile = createLazyRoute(() => import('views/employer/profile/EmployerProfile'), LOADING_MESSAGES.PROFILE)
const CandidateProfile = createLazyRoute(() => import('views/employer/profile/CandidateProfile'), LOADING_MESSAGES.PROFILE)
const YourJobPosts = createLazyRoute(() => import('views/employer/your-job-posts'), LOADING_MESSAGES.JOBS)
const JobsWithCandidates = createLazyRoute(() => import('views/employer/your-job-posts/JobsWithCandidates'), LOADING_MESSAGES.JOBS)
const YourJobPostsContainer = createLazyRoute(() => import('views/employer/your-job-posts/YourJobPostsContainer'), LOADING_MESSAGES.JOBS)
const YourApplicants = createLazyRoute(() => import('views/employer/your-job-posts/your-applicants'), LOADING_MESSAGES.CANDIDATES)
const Help = createLazyRoute(() => import('views/employer/Help/Help'), LOADING_MESSAGES.HELP)

// Contractor components
const FindJobs = createLazyRoute(() => import('views/contractor/find-jobs/index'), LOADING_MESSAGES.JOBS)
const JobDetail = createLazyRoute(() => import('views/contractor/find-jobs/jobDetail'), LOADING_MESSAGES.JOBS)
const ContractorLayout = createLazyRoute(() => import('views/contractor/index'), LOADING_MESSAGES.CONTRACTOR)
const ContractorMessages = createLazyRoute(() => import('views/contractor/messages/index'), LOADING_MESSAGES.MESSAGES)
const ContractorProfileContainer = createLazyRoute(() => import('views/contractor/profile/index'), LOADING_MESSAGES.PROFILE)
const ContractorProfile = createLazyRoute(() => import('views/contractor/profile/ContractorProfile'), LOADING_MESSAGES.PROFILE)
const ContractorEmployerProfile = createLazyRoute(() => import('views/contractor/profile/ContractorEmployerProfile'), LOADING_MESSAGES.PROFILE)
const ContractorSettings = createLazyRoute(() => import('views/contractor/setting/index'), LOADING_MESSAGES.SETTINGS)
const YourApplications = createLazyRoute(() => import('views/contractor/your-applications/index'), LOADING_MESSAGES.JOBS)
const HelpContractor = createLazyRoute(() => import('views/contractor/Help/Help'), LOADING_MESSAGES.HELP)

// Public pages
const TermsOfService = createLazyRoute(() => import('views/terms'), LOADING_MESSAGES.PUBLIC)
const PrivacyPolicy = createLazyRoute(() => import('views/privacy'), LOADING_MESSAGES.PUBLIC)
const JobDetailNonAuth = createLazyRoute(() => import('views/contractor/find-jobs/JobDetailNonAuth'), LOADING_MESSAGES.JOBS)
const CandidateProfileNonAuth = createLazyRoute(() => import('views/employer/profile/CandidateProfileNonAuth'), LOADING_MESSAGES.PROFILE)
const LatestHomePage = createLazyRoute(() => import('views/LatestHomePage'), LOADING_MESSAGES.PUBLIC)
const JobRedirect = createLazyRoute(() => import('views/contractor/find-jobs/JobRedirect'), LOADING_MESSAGES.JOBS)
const AccessbilityStatement = createLazyRoute(() => import('views/AccessibilityStatement'), LOADING_MESSAGES.PUBLIC)
const NewHomePageContainer = createLazyRoute(() => import('views/NewHomePage/NewHomePageContainer'), LOADING_MESSAGES.PUBLIC)
const SearchResultsContainer = createLazyRoute(() => import('views/NewHomePage/SearchResultsContainer'), LOADING_MESSAGES.PUBLIC)


const router = createBrowserRouter([
  {
    path: '/auth',
    element: <AuthLayout />,
    children: [
      {
        index: true,
        element: <FirstPage />,
      },
      {
        path: 'login',
        element: <Login />,
      },
      {
        path: 'register',
        element: <Register />,
      },
      {
        path: 'employee/register',
        element: <ContractorRegisterForm />,
      },
      {
        path: 'employer/register',
        element: <EmployeeRegisterForm />,
      },
      {
        path: 'reset-password',
        element: <ResetPassword />,
      },
      {
        path: 'verify',
        element: <Verification />,
      },
      {
        path: 'code-verification',
        element: <CodeVerification />,
      },
      {
        path: 'magic-link-verification',
        element: <MagicLinkVerification />,
      },
      {
        path: 'forgot-password',
        element: <ForgotPassword />,
      },
      {
        path: 'google-register',
        element: <GoogleSignUpForm />,
      },
    ],
  },
  {
    element: <AuthRoutes />,
    children: [
      {
        path: 'employer',
        element: <EmployerLayout />,
        children: [
          {
            path: 'add-job-post',
            element: <AddJobPost />,
          },
          {
            path: 'pricing-plans',
            element: <PricingPlans />,
          },
          {
            path: 'checkout-plan',
            element: <CheckoutPlan />,
          },
          {
            path: 'pricing-plans-in-app',
            element: <PricingPlansInApp />,
          },
          {
            path: 'find-candidates',
            element: <FindCandidates />,
          },
          {
            path: 'setting',
            element: <Settings />,
          },
          {
            path: 'job-posts',
            element: <YourJobPostsContainer />,
            children: [
              {
                index: true,
                element: <JobsWithCandidates />
                ,
              },
              {
                path: ':id/edit',
                element: <YourJobPosts />
                ,
              },
              {
                path: 'applicants/:id',
                element: <YourApplicants />
                ,
              },
            ],
          },
          {
            path: 'profile',
            element: <EmployerProfileContainer />
            ,
            children: [
              {
                index: true,
                element: <EmployerProfile />
                ,
              },
              {
                path: ':id',
                element: <CandidateProfile />
                ,
              },
            ],
          },
          {
            path: 'messages',
            element: <EmployerMessages />
            ,
          },
          {
            path: 'upgrade',
            element: <Upgrade />
            ,
          },
          {
            path: 'premium-video',
            element: <PremiumVideo />
            ,
          },
          {
            path: 'help',
            element: <Help />,
          },
        ],
      },
      {
        path: 'jobseeker',
        element: <ContractorLayout />
        ,
        children: [
          {
            element: <FindJobs />
            ,
            path: 'jobs',
          },
          {
            element: <JobDetail />
            ,
            path: 'jobs/:slugAndId',
          },
          {
            path: 'your-applications',
            element: <YourApplications />
            ,
          },
          {
            path: 'setting',
            element: <ContractorSettings />
            ,
          },
          {
            path: 'profile',
            element: <ContractorProfileContainer />
            ,
            children: [
              {
                index: true,
                element: <ContractorProfile />
                ,
              },
              {
                path: ':id',
                element: <ContractorEmployerProfile />
                ,
              },
            ],
          },
          {
            path: 'messages',
            element: <ContractorMessages />
            ,
          },
          {
            path: 'help',
            element: <HelpContractor />,
          },
        ],
      },
    ],
  },
  {
    path: '/',
    element: <LatestHomePage />,
  },
  {
    path: '/new',
    element: <NewHomePageContainer />,
  },
  {
    path: '/search-results',
    element: <SearchResultsContainer />,
  },
  {
    path: '/terms-of-service',
    element: <TermsOfService />,
  },
  {
    path: '/privacy-policy',
    element: <PrivacyPolicy />,
  },
  {
    path: '/accessbility-statement',
    element: <AccessbilityStatement />,
  },
  // {
  //   path: '/blog/:id',
  //   element: <BlogPost />,
  // },
  {
    path: '/home/job/:id([0-9a-fA-F-]{36})',
    element: <JobRedirect />,
  },

  {
    path: '/home/job/:slugAndId',
    element: <JobDetailNonAuth />,
  },
  {
    path: '/home/candidate/:id',
    element: <CandidateProfileNonAuth />,
  },
])

export default router
